// Geteiltes Typ→Icon/Label-Mapping für Benachrichtigungen (Toast, Glocken-
// Popover, Dashboard-Mitteilungszentrum). Eine einzige Quelle der Wahrheit für
// alle Mitteilungs-Typen (AC6): @-Erwähnungen/Notizen, eingehende Anrufe,
// eingehende Mails, News, TWV/Frist-Mitteilungen und offene Entwürfe.
// Unbekannte Typen fallen auf ein neutrales Default zurück.
import * as React from "react";
import {
    Mention20Regular,
    ArrowReply20Regular,
    Chat20Regular,
    Mail20Regular,
    MailCheckmark20Regular,
    MailInbox20Regular,
    CalendarLtr20Regular,
    Call20Regular,
    Megaphone20Regular,
    Clock20Regular,
    CalendarClock20Regular,
    DocumentEdit20Regular,
    ShieldKeyhole20Regular,
    ShieldCheckmark20Regular,
    Alert20Regular,
    TaskListSquareLtr20Regular,
    Beaker20Regular,
    DocumentAdd20Regular,
    CommentAdd20Regular,
    PersonAdd20Regular,
} from "@fluentui/react-icons";

/** Ein Eintrag im Typ-Mapping: Icon-Element + kurzes deutsches Label. */
type NotifTypeMeta = { icon: React.ReactNode; label: string };

// Zentrale Map Notification.type → Icon + Label. Schlüssel sind die im Backend
// erzeugten Typen (mention/reply, mail_incoming, news, twv, frist) sowie die
// vorbereiteten Typen für Anrufe (call) und offene Entwürfe (entwurf).
export const NOTIF_TYPE_MAP: Record<string, NotifTypeMeta> = {
    mention: { icon: <Mention20Regular />, label: "Erwähnung" },
    reply: { icon: <ArrowReply20Regular />, label: "Antwort" },
    chat_mention: { icon: <Chat20Regular />, label: "Chat-Erwähnung" },
    chat_dm: { icon: <Chat20Regular />, label: "Chat-Nachricht" },
    // PO 07.09.2026 — Nachricht in einer Gruppe oder einem Kanal (ohne Erwähnung).
    chat_nachricht: { icon: <Chat20Regular />, label: "Chat-Nachricht" },
    call: { icon: <Call20Regular />, label: "Anruf" },
    // Eingehende Mail (mail_incoming) ist der Backend-Typ; "mail" bleibt als
    // Alias für etwaige Altdaten erhalten.
    mail_incoming: { icon: <MailInbox20Regular />, label: "E-Mail" },
    mail: { icon: <Mail20Regular />, label: "E-Mail" },
    // Automatisch an den Vorgang gehängte Antwort (Thread-Vererbung).
    vorgang_mail_reply: { icon: <ArrowReply20Regular />, label: "Antwort im Vorgang" },
    // Mail einem Vorgang zugeordnet (MailLinkService) — der Klick öffnet die Mail.
    mail_vorgang: { icon: <Mail20Regular />, label: "Mail im Vorgang" },
    // T-23: Bestätigung an den Absender, dass seine gesendete Mail am Vorgang liegt.
    mail_vorgang_eigen: { icon: <MailCheckmark20Regular />, label: "Eigene Mail abgelegt" },
    // PO 28.09.2026: „Notification bei neuen Dokumenten, etc."
    dokument_neu: { icon: <DocumentAdd20Regular />, label: "Neues Dokument" },
    kommentar_neu: { icon: <CommentAdd20Regular />, label: "Neuer Kommentar" },
    // PO 28.09.2026: „Erinnerung Vorgänge am Tag" — werktags 07:30, eine je Person.
    twv_heute: { icon: <Clock20Regular />, label: "Heute fällig" },
    news: { icon: <Megaphone20Regular />, label: "News" },
    twv: { icon: <Clock20Regular />, label: "Wiedervorlage" },
    frist: { icon: <CalendarClock20Regular />, label: "Frist" },
    entwurf: { icon: <DocumentEdit20Regular />, label: "Entwurf" },
    termin: { icon: <CalendarLtr20Regular />, label: "Termin" },
    // Bitte um Freigabe an einem Kontakt, den der Antragsteller nicht ändern darf.
    approval_request: { icon: <ShieldKeyhole20Regular />, label: "Genehmigung erbeten" },
    // Genehmigt/abgelehnt/entzogen — an den Antragsteller (30.09.2026).
    approval_decision: { icon: <ShieldCheckmark20Regular />, label: "Freigabe entschieden" },
    // J4/E05 — Erwähnung in einem Mail-Kommentar bzw. eine mit mir geteilte Mail.
    mail_mention: { icon: <Mention20Regular />, label: "Mail-Erwähnung" },
    "mail-share": { icon: <MailInbox20Regular />, label: "Mail geteilt" },
    // H4 — eine Mail-Regel hat selbst einen Vorgang angelegt.
    vorgang_regel_neu: { icon: <TaskListSquareLtr20Regular />, label: "Vorgang aus Regel" },
    // Selbstdiagnose: `POST /api/notifications/test` bzw. `kmvsPush.test()`.
    test: { icon: <Beaker20Regular />, label: "Test" },
    // PO 07.09.2026 — an einem Vorgang/To-do eingetragen worden.
    vorgang_neu: { icon: <TaskListSquareLtr20Regular />, label: "Neuer Vorgang" },
    vorgang_zuweisung: { icon: <PersonAdd20Regular />, label: "Vorgang zugewiesen" },
    todo_neu: { icon: <TaskListSquareLtr20Regular />, label: "Neues To-do" },
    todo_zuweisung: { icon: <PersonAdd20Regular />, label: "To-do zugewiesen" },
    // Tickets (Teil 1): neu/Kommentar/Ergänzung an Admins, Statuswechsel an den Ersteller.
    ticket_neu: { icon: <TaskListSquareLtr20Regular />, label: "Neues Ticket" },
    ticket_eintrag: { icon: <Chat20Regular />, label: "Ticket kommentiert" },
    ticket_status: { icon: <DocumentEdit20Regular />, label: "Ticket-Status geändert" },
    // Tickets Teil 2: Claude hat Analyse + Plan zu einem Ticket-Stand fertig — Glocke an alle Admins.
    ticket_vorschlag: { icon: <TaskListSquareLtr20Regular />, label: "Lösungsvorschlag da" },
    // Office (SharePoint): Kosten-Mitteilung an die Betreiber — Warnschwelle, Budget oder
    // Mengen-Richtwert überschritten; die Bearbeitung läuft weiter (PO 29.10.2026).
    // reMarkable: Anlegen gestört oder wieder in Ordnung — nur beim Wechsel, an die Admins.
    remarkable_stoerung: { icon: <Alert20Regular />, label: "reMarkable" },
    spe_kosten: { icon: <Alert20Regular />, label: "Office-Kosten" },
    // Bis 29.10.2026: Erinnerung, den Azure-Kostenabgleich zu erneuern (nur noch Altbestand).
    spe_kostenabgleich: { icon: <Alert20Regular />, label: "Office-Kostenabgleich" },
};

// Neutrales Default für unbekannte Typen.
const NOTIF_DEFAULT: NotifTypeMeta = { icon: <Alert20Regular />, label: "Mitteilung" };

const metaFor = (type?: string | null): NotifTypeMeta =>
    (type && NOTIF_TYPE_MAP[type]) || NOTIF_DEFAULT;

/** Icon-Element zu einem Notification.type. */
export const notifIcon = (type?: string | null): React.ReactNode => metaFor(type).icon;

/** Deutsches Label zu einem Notification.type. */
export const notifLabel = (type?: string | null): string => metaFor(type).label;

/**
 * Typen, die im Mitteilungszentrum EINZELN als „Mention" angezeigt werden
 * (Notiz-/Aufgabenchat-Erwähnung + Antwort, interner Chat, Genehmigungs-Bitte).
 * Mail/News/TWV/Frist erscheinen NICHT einzeln — Mails werden als aggregierte
 * Zeile gezeigt. Glocke, Toast und Dashboard filtern hart auf diese Menge: ein
 * fehlender Eintrag heißt, die Mitteilung erscheint NIRGENDS in der Oberfläche.
 */
export const MENTION_TYPES = new Set<string>([
    "mention",
    "reply",
    "chat_mention",
    "chat_dm",
    // PO 07.09.2026: Jede Chat-Nachricht meldet sich bei allen außer dem
    // Absender — auch in Gruppen und Kanälen, wo bisher nur die namentlich
    // Erwähnten überhaupt etwas erfuhren.
    "chat_nachricht",
    "approval_request",
    // Entscheidung über die eigene Freigabe-Anfrage (30.09.2026). Ohne Eintrag
    // hier entstünde die Zeile, bliebe aber in der Oberfläche unsichtbar.
    "approval_decision",
    // J4/E05: Beide Mail-Typen erzeugten schon vorher Zeilen in der Tabelle —
    // ohne Eintrag hier erschienen sie in KEINER Oberfläche.
    "mail_mention",
    "mail-share",
    // H4: dasselbe gilt für den Vorgang aus einer Mail-Regel. Ohne diesen
    // Eintrag käme die Mitteilung weder in der Glocke noch im Dashboard an.
    // Die Menge steuert zugleich den Toast — die Amoklauf-Bremse von 25
    // Vorgängen je Regel und Tag deckelt daher auch die Toast-Menge.
    "vorgang_regel_neu",
    // Testmitteilung der Selbstdiagnose (kmvsPush.test()). Ohne Eintrag hier
    // liefe der Test ins Leere: die Zeile entstünde, wäre aber unsichtbar —
    // genau der Fehler, den der Test aufdecken soll.
    "test",
    // PO 07.09.2026 — „Trägt mich jemand an einem Vorgang oder To-do ein, muss
    // ich benachrichtigt werden." `todo_neu` gibt es seit dem 02.08.2026, es
    // erzeugte aber nur eine Tabellenzeile und eine Browser-Push: ohne Eintrag
    // hier blieb die Mitteilung in der Oberfläche unsichtbar.
    "vorgang_neu",
    "vorgang_zuweisung",
    "todo_neu",
    "todo_zuweisung",
    // Tickets (Teil 1): neu/Kommentar/Ergänzung an Admins, Statuswechsel an den Ersteller.
    "ticket_neu",
    "ticket_eintrag",
    "ticket_status",
    // Tickets Teil 2: Analyse/Plan durch Claude ist fertig.
    "ticket_vorschlag",
    // PO 28.09.2026 — Ereignisse AM Vorgang an seine Beteiligten. Die beiden
    // Mail-Typen entstanden schon länger, kamen aber nur als Browser-Push an;
    // hier fehlten sie, also stand in der Glocke nichts davon.
    "mail_vorgang",
    "vorgang_mail_reply",
    // T-23: Bestätigung an den Absender, dass die gesendete Mail am Vorgang
    // liegt (ohne Push).
    "mail_vorgang_eigen",
    "dokument_neu",
    "kommentar_neu",
    // Morgendliche Wiedervorlage-Erinnerung (eine Zeile je Person und Tag).
    "twv_heute",
    // Office-Kosten über einer Schwelle — nur an die SPE-Betreiber.
    "spe_kosten",
    "spe_kostenabgleich",
    "remarkable_stoerung",
]);

/** Ist dieser Notification-Typ eine einzeln anzuzeigende „Mention"? */
export const isMentionNotif = (type?: string | null): boolean => !!type && MENTION_TYPES.has(type);

/** Icon in getöntem, abgerundetem Quadrat (Aubergine-Akzent) — einheitlicher Look. */
export const NotifIconBadge: React.FC<{ type?: string | null; size?: number }> = ({ type, size = 36 }) => (
    <span
        style={{
            flexShrink: 0,
            width: size,
            height: size,
            borderRadius: 11,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(125,33,105,0.10)",
            color: "#7d2169",
        }}
    >
        {notifIcon(type)}
    </span>
);
